import { useEtapasKanban } from '@/shared/state/EtapasKanbanContext';
import { QRCodeSVG } from 'qrcode.react';
import { type OrcamentoDetalhe } from '@/features/orcamentos/api/orcamentosApi';
import type { DadosEmpresa } from '@/features/configuracoes/api/configuracoesApi';

type Props = {
  detalhe: OrcamentoDetalhe;
  nomeEmpresa: string;
  empresa?: DadosEmpresa | null;
  termo?: string | null;
  /**
   * Link público de acompanhamento da OS (token com validade configurável, 1–30 dias). Quando presente,
   * o cupom fecha com um QR code para o cliente escanear. Ausente (offline
   * ou OS ainda não sincronizada), o bloco simplesmente não é impresso.
   */
  linkAcompanhamento?: string | null;
};

const moeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const dataFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function formatarDesconto(tipo: string | null, valor: number | null): string | null {
  if (!tipo || valor == null || valor === 0) return null;
  return tipo === 'Percentual' ? `${valor}%` : moeda.format(valor);
}

export function CupomTermico58mm({
  detalhe,
  nomeEmpresa,
  empresa,
  termo,
  linkAcompanhamento,
}: Props) {
  const { rotuloDe } = useEtapasKanban();
  const pecas = detalhe.itens.filter((i) => i.tipo === 'Produto');
  const servicos = detalhe.itens.filter((i) => i.tipo === 'Servico');
  const temObs = detalhe.observacoes && detalhe.observacoes.trim().length > 0;
  // Trata "<p></p>" (saída padrão do TipTap quando vazio) também como nulo.
  const termoLimpo = (() => {
    const limpo = termo?.replace(/<p>\s*<\/p>/g, '').trim() ?? '';
    return limpo.length > 0 ? limpo : null;
  })();
  const nome = empresa?.nomeEmpresa?.trim() || nomeEmpresa;
  const link = linkAcompanhamento?.trim() || null;
  const statusRotulo = rotuloDe(detalhe.status);
  const linhaEndereco = [empresa?.endereco, empresa?.numero, empresa?.bairro]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="cupom-58">
      <div className="cupom-58__empresa">{nome}</div>
      {empresa?.cnpj && (
        <div className="cupom-58__sub">CNPJ: {empresa.cnpj}</div>
      )}
      {empresa?.telefone && (
        <div className="cupom-58__sub">Tel: {empresa.telefone}</div>
      )}
      {linhaEndereco && (
        <div className="cupom-58__sub">{linhaEndereco}</div>
      )}
      {(empresa?.cidade || empresa?.estado) && (
        <div className="cupom-58__sub">
          {[empresa.cidade, empresa.estado].filter(Boolean).join(' - ')}
        </div>
      )}
      <div className="cupom-58__sub">OS {detalhe.numero}</div>
      <div className="cupom-58__sub">{dataFmt.format(new Date(detalhe.data))}</div>

      <Divisor />

      <div className="cupom-58__status">
        <div className="cupom-58__status-rotulo">STATUS</div>
        <div className="cupom-58__status-valor">{statusRotulo}</div>
      </div>

      <Divisor />

      <Linha rotulo="Cliente" valor={detalhe.clienteNome} />
      {detalhe.clienteTelefone && (
        <Linha rotulo="Telefone" valor={detalhe.clienteTelefone} />
      )}
      <Linha
        rotulo={detalhe.placa ? 'Placa' : 'Veículo'}
        valor={detalhe.veiculoDescricao}
      />
      {detalhe.kmRegistrado != null && (
        <Linha
          rotulo="KM"
          valor={`${detalhe.kmRegistrado.toLocaleString('pt-BR')} km`}
        />
      )}
      {temObs && (
        <div className="cupom-58__bloco">
          <div className="cupom-58__rotulo">Obs.:</div>
          <div className="cupom-58__obs">{detalhe.observacoes}</div>
        </div>
      )}

      {servicos.length > 0 && (
        <>
          <Divisor />
          <div className="cupom-58__secao">SERVIÇOS</div>
          {servicos.map((s) => (
            <ItemLinha
              key={s.id}
              descricao={s.descricao}
              quantidade={s.quantidade}
              preco={s.precoUnitario}
              desconto={formatarDesconto(s.descontoTipo, s.descontoValor)}
              subtotal={s.subtotal}
            />
          ))}
        </>
      )}

      {pecas.length > 0 && (
        <>
          <Divisor />
          <div className="cupom-58__secao">PEÇAS</div>
          {pecas.map((p) => (
            <ItemLinha
              key={p.id}
              descricao={p.descricao}
              quantidade={p.quantidade}
              preco={p.precoUnitario}
              desconto={formatarDesconto(p.descontoTipo, p.descontoValor)}
              subtotal={p.subtotal}
            />
          ))}
        </>
      )}

      <Divisor />

      {detalhe.valorDescontoGeral > 0 && (
        <div className="cupom-58__linha">
          <span>Desconto geral</span>
          <span>− {moeda.format(detalhe.valorDescontoGeral)}</span>
        </div>
      )}
      <div className="cupom-58__total">
        <span>TOTAL</span>
        <span>{moeda.format(detalhe.total)}</span>
      </div>

      {termoLimpo && (
        <>
          <Divisor />
          <div className="cupom-58__secao">TERMOS E CONDIÇÕES</div>
          <div
            className="cupom-58__termo"
            dangerouslySetInnerHTML={{ __html: termoLimpo }}
          />
        </>
      )}

      {link && (
        <>
          <Divisor />
          <div className="cupom-58__secao">ACOMPANHE SEU SERVIÇO</div>
          <div className="cupom-58__qrcode">
            <QRCodeSVG
              value={link}
              size={132}
              // Nível M: tolerância suficiente para a impressão térmica sem
              // encher o código de módulos pequenos demais para o cabeçote.
              level="M"
              // Zona silenciosa dentro do próprio SVG — sem ela o leitor
              // depende da margem do papel, que o corte do cupom pode comer.
              marginSize={2}
              bgColor="#ffffff"
              fgColor="#000000"
            />
          </div>
          <div className="cupom-58__qrcode-legenda">
            Aponte a câmera do celular para ver o status da sua ordem de serviço.
          </div>
        </>
      )}

      <Divisor />

      <div className="cupom-58__rodape">{nomeEmpresa}</div>
    </div>
  );
}

function Divisor() {
  return <div className="cupom-58__divisor">--------------------------------</div>;
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="cupom-58__bloco">
      <span className="cupom-58__rotulo">{rotulo}: </span>
      <span>{valor}</span>
    </div>
  );
}

function ItemLinha({
  descricao,
  quantidade,
  preco,
  desconto,
  subtotal,
}: {
  descricao: string;
  quantidade: number;
  preco: number;
  desconto: string | null;
  subtotal: number;
}) {
  return (
    <div className="cupom-58__item">
      <div className="cupom-58__item-desc">{descricao}</div>
      <div className="cupom-58__linha">
        <span>
          {quantidade} x {moeda.format(preco)}
        </span>
        <span>{moeda.format(subtotal)}</span>
      </div>
      {desconto && (
        <div className="cupom-58__linha cupom-58__linha--muted">
          <span>Desc.</span>
          <span>{desconto}</span>
        </div>
      )}
    </div>
  );
}
